// The dashboard's grid, arranged by hand as in the AWS console: a widget moves by
// its header, resizes from its bottom right corner, leaves through its menu, and
// joins by being dragged in from the widget panel. Every change is saved as the
// hand lets go (XomicsOverview.js). Only the full four-column grid can be arranged;
// narrower screens show the same arrangement folded to two columns.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { GripVertical, LayoutGrid, Plus, X } from 'lucide-react';
import { Responsive, useContainerWidth } from 'react-grid-layout';
import { GridBackground } from 'react-grid-layout/extras';

import { GRID, t } from './DashboardData';
import { EmptyNote, ReadyContext, TileContext } from './DashboardParts';
import { GROUPS, WIDGETS, widgetAbout, widgetTitle } from './DashboardWidgets';

const { rowHeight: ROW_HEIGHT, gap: GAP } = GRID;
// The longest the shimmer waits for a slow widget before showing the page anyway.
const REVEAL_CAP_MS = 6000;

// Where a tile sits, in pixels, on a grid `width` wide with `columns` columns.
function boxOf(item, width, columns) {
  const column = (width - (GAP * (columns - 1))) / columns;
  return {
    left: item.x * (column + GAP), top: item.y * (ROW_HEIGHT + GAP),
    width: (item.w * column) + ((item.w - 1) * GAP), height: (item.h * ROW_HEIGHT) + ((item.h - 1) * GAP),
  };
}

// Until every widget has its first answer, a shimmering card stands where each will be.
function Shimmer({ items, width, columns }) {
  const height = Math.max(0, ...items.map((item) => ((item.y + item.h) * (ROW_HEIGHT + GAP)) - GAP));
  return (
    <div className="xo-dash-shimmer" style={{ height }} aria-busy="true" aria-label={t('page.loading')}>
      {items.map((item) => (
        <div key={item.i} className="xo-dash-shimmer-tile" style={boxOf(item, width, columns)}>
          <i style={{ width: '38%' }} /><i className="is-figure" style={{ width: '28%' }} /><i className="is-block" />
        </div>
      ))}
    </div>
  );
}
// The grid folds to two columns when narrower than this.
const BREAKPOINTS = { full: 880, narrow: 0 };
const NARROW_COLUMNS = 2;
const DROPPING = '__dropping__';

// The corner a widget is resized from: two short diagonal strokes.
function resizeHandle(axis, ref) {
  return (
    <span ref={ref} className={`xo-dash-resize react-resizable-handle react-resizable-handle-${axis}`} title={t('edit.resize')} aria-hidden="true">
      <svg viewBox="0 0 12 12"><path d="M11 4 4 11M11 8 8 11" /></svg>
    </span>
  );
}

const placesOf = (layout) => layout.filter((item) => item.i !== DROPPING).map(({ i, x, y, w, h }) => ({ code: i, x, y, w, h }));

// A narrower screen folds the arrangement into `columns` in reading order, row by
// row, as the server lays out a role default; it is shown, never saved.
function folded(items, columns) {
  const out = [];
  let x = 0;
  let y = 0;
  let rowHeight = 0;
  for (const item of [...items].sort((a, b) => a.y - b.y || a.x - b.x)) {
    const w = Math.min(item.w, columns);
    if (x + w > columns) {
      x = 0;
      y += rowHeight;
      rowHeight = 0;
    }
    out.push({ ...item, x, y, w });
    x += w;
    rowHeight = Math.max(rowHeight, item.h);
  }
  return out;
}

// The bottom of an arrangement, in rows: where a widget added by button goes.
export const bottomOf = (widgets) => widgets.reduce((most, widget) => Math.max(most, widget.y + widget.h), 0);

function Tile({ entry, editable, movable, permissions, onRemove }) {
  const { Component } = WIDGETS[entry.code];
  const name = widgetTitle(entry.code);
  const remove = useCallback(() => onRemove(entry.code), [onRemove, entry.code]);
  const tile = useMemo(() => ({ editable, movable, onRemove: remove, name }), [editable, movable, remove, name]);
  return (
    <TileContext.Provider value={tile}>
      <Component width={entry.w} height={entry.h} permissions={permissions} />
    </TileContext.Provider>
  );
}

export default function DashboardGrid({ widgets, library, columns, editable, permissions, dropping, onChange, onDropped, onAdd }) {
  const { width, containerRef, mounted } = useContainerWidth();
  const specs = useMemo(() => new Map(library.map((spec) => [spec.code, spec])), [library]);
  const shown = useMemo(() => widgets.filter((entry) => WIDGETS[entry.code] && specs.has(entry.code)), [widgets, specs]);
  const movable = editable && mounted && width >= BREAKPOINTS.full;
  const layouts = useMemo(() => {
    const full = shown.map((entry) => {
      const spec = specs.get(entry.code);
      return { i: entry.code, x: entry.x, y: entry.y, w: entry.w, h: entry.h, minW: spec.minWidth, maxW: spec.maxWidth, minH: spec.minHeight, maxH: spec.maxHeight };
    });
    return { full, narrow: folded(full, NARROW_COLUMNS) };
  }, [shown, specs]);

  // The page shows once no widget's first read is still waiting (or after the cap),
  // all at once, so nothing moves or fills in piece by piece.
  const waiting = useRef(new Set());
  const [released, setReleased] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const ready = useMemo(() => ({
    hold: (key) => { waiting.current.add(key); },
    release: (key) => { if (waiting.current.delete(key)) setReleased((count) => count + 1); },
  }), []);
  useEffect(() => {
    if (revealed || !mounted || waiting.current.size > 0) return undefined;
    const frame = window.requestAnimationFrame(() => setRevealed(true));
    return () => window.cancelAnimationFrame(frame);
  }, [released, mounted, revealed]);
  useEffect(() => {
    const timer = window.setTimeout(() => setRevealed(true), REVEAL_CAP_MS);
    return () => window.clearTimeout(timer);
  }, []);

  // While a tile is held, the grid's cells show behind the tiles, as far down as
  // it could be put.
  const [holding, setHolding] = useState(false);
  const hold = useCallback(() => setHolding(true), []);
  // Letting go ends the hold even when the tile never moved and the grid reports no stop.
  useEffect(() => {
    if (!holding) return undefined;
    const release = () => window.setTimeout(() => setHolding(false), 0);
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    return () => { window.removeEventListener('pointerup', release); window.removeEventListener('pointercancel', release); };
  }, [holding]);
  const remove = useCallback((code) => onChange(widgets.filter((entry) => entry.code !== code)), [onChange, widgets]);
  // A move or a resize is saved once the hand lets go, as the grid then stands.
  const settle = useCallback((layout) => {
    setHolding(false);
    if (movable) onChange(placesOf(layout));
  }, [movable, onChange]);
  const drop = useCallback((layout, item) => {
    if (!dropping || !item) return;
    onChange(placesOf(layout).concat({ code: dropping.code, x: item.x, y: item.y, w: item.w, h: item.h }));
    onDropped();
  }, [dropping, onChange, onDropped]);
  const showCells = movable && (holding || dropping !== null);

  if (shown.length === 0 && !dropping) {
    return (
      <div className="xo-dash-blank">
        <EmptyNote icon={LayoutGrid} title={t('empty.title')} detail={t('empty.detail')} />
        {editable && <button type="button" className="xo-dash-primary" onClick={onAdd}><Plus aria-hidden="true" />{t('edit.add_widget')}</button>}
      </div>
    );
  }

  const full = mounted && width >= BREAKPOINTS.full;
  return (
    <ReadyContext.Provider value={ready}>
    <div ref={containerRef}
      className={`xo-dash-board${movable ? ' is-movable' : ''}${dropping ? ' is-dropping' : ''}${showCells ? ' is-holding' : ''}${revealed ? ' is-revealed' : ''}`}>
      {mounted && !revealed && <Shimmer items={full ? layouts.full : layouts.narrow} width={width} columns={full ? columns : NARROW_COLUMNS} />}
      {mounted && showCells && (
        <GridBackground className="xo-dash-cells" width={width} cols={columns} rowHeight={ROW_HEIGHT} margin={[GAP, GAP]} containerPadding={[0, 0]}
          rows={bottomOf(widgets) + (dropping ? 4 : 2)} borderRadius={14} />
      )}
      {mounted && (
        <Responsive
          width={width}
          layouts={layouts}
          breakpoints={BREAKPOINTS}
          cols={{ full: columns, narrow: NARROW_COLUMNS }}
          margin={[GAP, GAP]}
          containerPadding={[0, 0]}
          rowHeight={ROW_HEIGHT}
          dragConfig={{ enabled: movable, handle: '.xo-dash-grip', cancel: 'a, button, .xo-dash-menu', threshold: 4 }}
          resizeConfig={{ enabled: movable, handles: ['se'], handleComponent: resizeHandle }}
          dropConfig={{
            enabled: movable && dropping !== null,
            defaultItem: dropping ? { w: dropping.defaultWidth, h: dropping.defaultHeight } : { w: 1, h: 4 },
            onDragOver: () => (dropping ? { w: dropping.defaultWidth, h: dropping.defaultHeight } : false),
          }}
          droppingItem={dropping ? { i: DROPPING, x: 0, y: 0, w: dropping.defaultWidth, h: dropping.defaultHeight } : undefined}
          onDragStart={hold}
          onResizeStart={hold}
          onDragStop={settle}
          onResizeStop={settle}
          onDrop={drop}
        >
          {shown.map((entry) => (
            <div key={entry.code} className="xo-dash-tile">
              <Tile entry={entry} editable={editable} movable={movable} permissions={permissions} onRemove={remove} />
            </div>
          ))}
        </Responsive>
      )}
    </div>
    </ReadyContext.Provider>
  );
}

// The widgets this person may add, by kind. Each is dragged onto the grid, or added
// at the bottom with its button. The panel leaves the grid free to drop on.
export function WidgetPanel({ library, placed, canDrag, onDragStart, onDragEnd, onAdd, onClose }) {
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  const byGroup = GROUPS.map((group) => ({
    group, items: library.filter((spec) => WIDGETS[spec.code] && WIDGETS[spec.code].group === group && !placed.has(spec.code)),
  })).filter((entry) => entry.items.length > 0);
  return (
    <motion.aside className="xo-dash-panel" role="dialog" aria-labelledby="xo-dash-panel-title"
      initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 24 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
      <header>
        <div>
          <h2 id="xo-dash-panel-title">{t('library.title')}</h2>
          <p>{t('library.drag_hint')}</p>
        </div>
        <button type="button" className="xo-dash-icon-button" onClick={onClose} aria-label={t('library.close')}><X aria-hidden="true" /></button>
      </header>
      <div className="xo-dash-panel-body">
        {byGroup.length === 0 && <EmptyNote icon={LayoutGrid} title={t('library.all_placed')} />}
        {byGroup.map(({ group, items }) => (
          <section key={group}>
            <h3>{t(`library.group_${group}`)}</h3>
            <ul>
              {items.map((spec) => {
                const Icon = WIDGETS[spec.code].icon;
                return (
                  <li key={spec.code} draggable={canDrag} unselectable="on" className={canDrag ? 'is-draggable' : ''}
                    onDragStart={(event) => { event.dataTransfer.setData('text/plain', spec.code); event.dataTransfer.effectAllowed = 'copy'; onDragStart(spec); }}
                    onDragEnd={onDragEnd}>
                    {canDrag && <GripVertical className="xo-dash-panel-grip" aria-hidden="true" />}
                    <span className="xo-dash-panel-icon" aria-hidden="true"><Icon /></span>
                    <span className="xo-dash-panel-text">
                      <strong>{widgetTitle(spec.code)}</strong>
                      <small>{widgetAbout(spec.code)}</small>
                    </span>
                    <button type="button" className="xo-dash-add" onClick={() => onAdd(spec)} aria-label={t('library.add', { name: widgetTitle(spec.code) })}>
                      <Plus aria-hidden="true" />{t('library.add_short')}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </motion.aside>
  );
}
